import {
  EventNotice,
  RecurringUnit,
  EventNotice_NoticeType,
  CalendarEvent,
} from "protogen/calendar_pb";
import { recurringUnitString } from "../../types/calendars";
import { numberToWord, pluralize } from "../../common/utils";
import { TimezoneAbbreviations } from "../common/Timezones";

import { CalendarEvents } from "./CalendarComponent";

import {
  format,
  startOfWeek,
  parseISO,
  endOfWeek,
  parse,
  startOfDay,
  differenceInDays,
} from "date-fns";

const isValidTimezone = (tz: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

const formatRecurringInterval = (
  unit: RecurringUnit,
  interval: number,
): string => {
  let unitStr = recurringUnitString(unit);
  // Pluralize the unit if interval is greater than 1
  let pluralUnit = interval > 1 ? pluralize(unitStr) : unitStr;
  // Format the interval (convert numbers to words for 1 and 2)
  let formattedInterval =
    interval === 1 ? "every" : `every ${numberToWord(interval)}`;
  return `${formattedInterval} ${pluralUnit}`;
};

const getDayName = (dayIndex: number): string => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days[dayIndex] || "";
};

const getOrdinal = (n: number): string => {
  if (n === 1) return "first";
  if (n === 2) return "second";
  if (n === 3) return "third";
  if (n === 4) return "fourth";
  if (n === 5) return "fifth";
  return `${n}th`;
};

export const formatRecurringDescription = (
  event: CalendarEvent | EventNotice,
): string => {
  if (
    !event.recurringUnit ||
    !event.recurringInterval ||
    event.recurringInterval === 0
  ) {
    return "";
  }

  const unit = event.recurringUnit;
  const interval = event.recurringInterval;
  const unitStr = recurringUnitString(unit);

  let description = "";

  // Check if this is a CalendarEvent (has the advanced recurring fields)
  const isCalendarEvent = "recurringByDay" in event;
  const recurringByDay = isCalendarEvent
    ? (event as CalendarEvent).recurringByDay
    : [];
  const recurringBySetPos = isCalendarEvent
    ? (event as CalendarEvent).recurringBySetPos
    : 0;
  const recurringByMonthDay = isCalendarEvent
    ? (event as CalendarEvent).recurringByMonthDay
    : [];
  const recurringCount = isCalendarEvent
    ? (event as CalendarEvent).recurringCount
    : 0;
  const recurringUntilSec = isCalendarEvent
    ? (event as CalendarEvent).recurringUntilSec
    : BigInt(0);

  // Weekly recurrence with specific days
  if (unit === RecurringUnit.RecurringUnit_WEEK && recurringByDay.length > 0) {
    const days = recurringByDay.map((d) => getDayName(d)).join(", ");
    if (interval === 1) {
      description = `Weekly on ${days}`;
    } else if (interval === 2) {
      description = `Every other week on ${days}`;
    } else {
      description = `Every ${interval} weeks on ${days}`;
    }
  }
  // Monthly recurrence with day of week pattern (e.g., "first Friday")
  else if (
    unit === RecurringUnit.RecurringUnit_MONTH &&
    recurringBySetPos > 0 &&
    recurringByDay.length > 0
  ) {
    const dayName = getDayName(recurringByDay[0]);
    const ordinal = getOrdinal(recurringBySetPos);
    if (interval === 1) {
      description = `Monthly on the ${ordinal} ${dayName}`;
    } else {
      description = `Every ${interval} months on the ${ordinal} ${dayName}`;
    }
  }
  // Monthly recurrence with specific day of month
  else if (
    unit === RecurringUnit.RecurringUnit_MONTH &&
    recurringByMonthDay.length > 0
  ) {
    const day = recurringByMonthDay[0];
    if (interval === 1) {
      description = `Monthly on day ${day}`;
    } else {
      description = `Every ${interval} months on day ${day}`;
    }
  }
  // Simple recurrence patterns
  else {
    if (interval === 1) {
      // Special case mapping for correct English
      if (unit === RecurringUnit.RecurringUnit_DAY) {
        description = "Daily";
      } else if (unit === RecurringUnit.RecurringUnit_WEEK) {
        description = "Weekly";
      } else if (unit === RecurringUnit.RecurringUnit_MONTH) {
        description = "Monthly";
      } else if (unit === RecurringUnit.RecurringUnit_YEAR) {
        description = "Yearly";
      } else {
        description = `${unitStr.charAt(0).toUpperCase() + unitStr.slice(1)}ly`;
      }
    } else if (interval === 2 && unit === RecurringUnit.RecurringUnit_WEEK) {
      description = "Every other week";
    } else {
      const pluralUnit = pluralize(unitStr);
      description = `Every ${interval} ${pluralUnit}`;
    }
  }

  // Add end condition
  if (recurringCount > 0) {
    description += `, ${recurringCount} times`;
  } else if (Number(recurringUntilSec) > 0) {
    const untilDate = new Date(Number(recurringUntilSec) * 1000);
    const formattedDate = format(untilDate, "MMM d, yyyy");
    description += `, until ${formattedDate}`;
  }

  return description;
};

export const recurringSubtitle = (event: EventNotice, withDate: boolean) => {
  let startStr: string;
  if (event.allDay) {
    const localDateOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };
    const allDayStr = getAllDdayStr(
      event.startDate,
      event.endDate,
      localDateOptions,
    );

    startStr = withDate ? `All day on ${allDayStr}` : "All day";
  } else {
    const start = new Date(Number(event.startSec) * 1000);
    startStr = start.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });
  }
  return `${startStr} (${formatRecurringInterval(
    event.recurringUnit,
    event.recurringInterval,
  )})`;
};

export const subtitle = (
  event: EventNotice,
  withDate: boolean,
  userTimezone?: string,
) => {
  if (event.noticeType === EventNotice_NoticeType.NoticeType_UPCOMING_TASK) {
    return "Task due";
  } else if (
    event.noticeType === EventNotice_NoticeType.NoticeType_OVERDUE_TASK
  ) {
    return "Task (overdue)";
  } else if (
    event.noticeType === EventNotice_NoticeType.NoticeType_RECURRING_EVENT
  ) {
    return recurringSubtitle(event, withDate);
  } else {
    return eventSubtitle(event, withDate, userTimezone);
  }
};

export const eventSubtitle = (
  event: EventNotice | CalendarEvent,
  withDate: boolean,
  userTimezone?: string,
) => {
  const displayTimezone =
    event.displayTimezone &&
    event.displayTimezone !== "UTC" &&
    isValidTimezone(event.displayTimezone)
      ? event.displayTimezone
      : undefined;
  const start = new Date(Number(event.startSec) * 1000);
  const end = new Date(Number(event.endSec) * 1000);

  // Use userTimezone if provided and valid, otherwise use browser's local timezone
  const primaryTimezone =
    userTimezone && isValidTimezone(userTimezone) ? userTimezone : undefined;

  const localDateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: primaryTimezone,
  };

  const localTimeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    timeZone: primaryTimezone,
  };

  const displayDateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: displayTimezone || undefined,
  };

  const displayTimeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    timeZone: displayTimezone || undefined,
  };

  const localStartDateStr = start.toLocaleDateString("en-US", localDateOptions);
  const localStartTimeStr = start.toLocaleTimeString("en-US", localTimeOptions);
  const localEndTimeStr = end.toLocaleTimeString("en-US", localTimeOptions);

  // Only show displayTimezone in parens if it's different from the primaryTimezone
  const showDisplayTimezone =
    displayTimezone && displayTimezone !== primaryTimezone;

  const displayStartDateStr = showDisplayTimezone
    ? start.toLocaleDateString("en-US", displayDateOptions)
    : "";
  const displayStartTimeStr = showDisplayTimezone
    ? start.toLocaleTimeString("en-US", displayTimeOptions)
    : "";
  const displayEndTimeStr = showDisplayTimezone
    ? end.toLocaleTimeString("en-US", displayTimeOptions)
    : "";

  let timezoneStr = "";
  if (showDisplayTimezone) {
    timezoneStr = ` ${TimezoneAbbreviations[displayTimezone]}`;
  }

  const datePrefix = withDate ? `${localStartDateStr}, ` : "";
  if (event.allDay && "startDate" in event) {
    const allDayStr = getAllDdayStr(
      event.startDate,
      event.endDate,
      localDateOptions,
    );
    return withDate
      ? `All day on ${allDayStr}${timezoneStr}`
      : `All day${timezoneStr}`;
  } else if (event.endSec) {
    if (
      localStartDateStr === end.toLocaleDateString("en-US", localDateOptions)
    ) {
      // Same day
      return showDisplayTimezone
        ? `${datePrefix}${localStartTimeStr} - ${localEndTimeStr} (${displayStartTimeStr} - ${displayEndTimeStr}${timezoneStr})`
        : `${datePrefix}${localStartTimeStr} - ${localEndTimeStr}`;
    } else {
      // Different days
      const localEndDateStr = end.toLocaleDateString("en-US", localDateOptions);
      return showDisplayTimezone
        ? `${datePrefix}${localStartTimeStr} - ${localEndDateStr}, ${localEndTimeStr} (${displayStartDateStr}, ${displayStartTimeStr} - ${displayEndTimeStr}${timezoneStr})`
        : `${datePrefix}${localStartTimeStr} - ${localEndDateStr}, ${localEndTimeStr}`;
    }
  } else {
    return showDisplayTimezone
      ? `${datePrefix}${localStartTimeStr} (${displayStartTimeStr}${timezoneStr})`
      : `${datePrefix}${localStartTimeStr}`;
  }
};

const getAllDdayStr = (
  startDate: string,
  endDate: string,
  formatOptions: Intl.DateTimeFormatOptions,
) => {
  const allDayStart = startOfDay(parse(startDate, "yyyy-MM-dd", new Date()));
  const allDayStartFormatted = allDayStart.toLocaleDateString(
    "en-US",
    formatOptions,
  );
  const allDayEnd = startOfDay(parse(endDate, "yyyy-MM-dd", new Date()));
  const allDayEndFormatted = allDayEnd.toLocaleDateString(
    "en-US",
    formatOptions,
  );
  const isMultiDay = differenceInDays(allDayEnd, allDayStart) > 0;
  return isMultiDay
    ? `${allDayStartFormatted} - ${allDayEndFormatted}`
    : allDayStartFormatted;
};

interface AttendeeStatus {
  status: number;
  label: string;
  labelColor: string | null;
  labelBackgroundColor: string | null;
}
const ATTENDEE_STATUS: AttendeeStatus[] = [
  {
    status: 1,
    label: "Accepted",
    labelColor: "primary.main",
    labelBackgroundColor: "#ECF4EC",
  },
  {
    status: 2,
    label: "Declined",
    labelColor: "secondary.dark",
    labelBackgroundColor: "secondary.light",
  },
  {
    status: 3,
    label: "Maybe",
    labelColor: null,
    labelBackgroundColor: null,
  },
  {
    status: 4,
    label: "Pending",
    labelColor: null,
    labelBackgroundColor: null,
  },
];
export const getAttendeeStatusLabel = (status: number) => {
  const attendeeObj = ATTENDEE_STATUS.find((p) => p.status === status);
  return attendeeObj ? attendeeObj.label : "Pending";
};
export const getAttendeeStatusLabelColor = (status: number) => {
  const attendeeObj = ATTENDEE_STATUS.find((p) => p.status === status);
  return attendeeObj ? attendeeObj.labelColor : null;
};
export const getAttendeeStatusLabelBackgroundColor = (status: number) => {
  const attendeeObj = ATTENDEE_STATUS.find((p) => p.status === status);
  return attendeeObj ? attendeeObj.labelBackgroundColor : null;
};

export const getMonthName = (date: Date) => {
  const monthIndex = date.getMonth();
  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return MONTH_NAMES[monthIndex];
};

export const getMonthBounds = (date: Date): [bigint, bigint] => {
  // First moment of the month
  const firstDayOfMonth = new Date(
    date.getFullYear(),
    date.getMonth(),
    1,
    0,
    0,
    0,
    0,
  );

  // Last moment of the month
  const lastDayOfMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  // Convert to seconds since the Unix Epoch
  const firstMomentInSeconds = BigInt(
    Math.floor(firstDayOfMonth.getTime() / 1000),
  );
  const lastMomentInSeconds = BigInt(
    Math.floor(lastDayOfMonth.getTime() / 1000),
  );

  return [firstMomentInSeconds, lastMomentInSeconds];
};

export const addOneMonth = (date: Date): Date => {
  let newYear = date.getFullYear();
  let newMonth = date.getMonth() + 1;
  let newDay = date.getDate();

  if (newMonth > 11) {
    newYear++;
    newMonth = 0;
  }
  let result = new Date(newYear, newMonth, newDay);

  if (result.getMonth() !== newMonth) {
    result = new Date(newYear, newMonth + 1, 0);
  }
  return result;
};

export const subtractOneMonth = (date: Date): Date => {
  let newYear = date.getFullYear();
  let newMonth = date.getMonth() - 1;
  let newDay = date.getDate();

  if (newMonth < 0) {
    newYear--;
    newMonth = 11;
  }

  let result = new Date(newYear, newMonth, newDay);
  if (result.getMonth() !== newMonth) {
    result = new Date(newYear, newMonth + 1, 0);
  }
  return result;
};

// View palette here:
// https://claude.ai/public/artifacts/62ab4a5e-0af3-439c-a103-767881048dfa
export const defaultColor = ["#D7ECD4", "black"]; // Fallback color
const predefinedColors = [
  ["#D0F1EB", "black"],
  ["#FDD7C6", "black"],
  ["#F0DDEA", "black"],
  ["#BBDBE9", "black"],
  ["#FEF1C1", "black"],
  ["#D7ECD4", "black"],
  ["#FFB8B6", "black"],
  ["#198282", "white"],
  ["#FFC3D4", "black"],
  ["#EF7B77", "white"],
  ["#996A8A", "white"],
  ["#6092A8", "white"],
  ["#F59F51", "white"],
  ["#468E3E", "white"],
  ["#C14743", "white"],
  // many more colors
  ["#5733FF", "white"], // Deep blue
  ["#FFC300", "black"], // Bright yellow
  ["#C70039", "white"], // Red
  ["#900C3F", "white"], // Dark purple
  ["#DAF7A6", "black"], // Light green
  ["#00FF00", "black"], // Lime green
  ["#008080", "white"], // Teal
  ["#FFA07A", "black"], // Light salmon
  ["#00CED1", "white"], // Dark turquoise
  ["#4682B4", "white"], // Steel blue
  ["#708090", "black"], // Slate gray
  ["#6B8E23", "white"], // Olive drab
  ["#FFD700", "black"], // Gold
  ["#8B0000", "white"], // Dark red
  ["#7FFF00", "black"], // Chartreuse
  ["#40E0D0", "black"], // Turquoise
  ["#000080", "white"], // Navy
  ["#800080", "white"], // Purple
  ["#A52A2A", "white"], // Brown
  ["#5F9EA0", "white"], // Cadet blue
  ["#20B2AA", "white"], // Light sea green
  ["#6495ED", "white"], // Cornflower blue
  ["#32CD32", "black"], // Lime green
  ["#2E8B57", "white"], // Sea green
  ["#B8860B", "black"], // Dark goldenrod
  ["#4169E1", "white"], // Royal blue
  ["#468499", "white"], // Teal-ish gray
  ["#DC143C", "white"], // Crimson
  ["#8A2BE2", "white"], // Blue violet
  ["#F4A460", "black"], // Sandy brown
  ["#FFB6C1", "black"], // Light pink
  ["#2C3E50", "#ECF0F1"], // Dark blue-gray with light gray
  ["#E8F5E8", "#2E7D32"], // Very light green with dark green
  ["#FFF3E0", "#E65100"], // Light orange with dark orange
  ["#F3E5F5", "#7B1FA2"], // Light purple with dark purple
  ["#E1F5FE", "#0277BD"], // Light cyan with dark blue
  ["#FFF8E1", "#F57F17"], // Light yellow with dark yellow
  ["#FFECB3", "#E65A00"], // Light amber with burnt orange
  ["#F1F8E9", "#33691E"], // Very light green with olive
  ["#FCE4EC", "#C2185B"], // Light pink with dark pink
  ["#E8EAF6", "#3F51B5"], // Light indigo with indigo
  ["#EFEBE9", "#5D4037"], // Light brown with dark brown
  ["#E0F2F1", "#00695C"], // Light teal with dark teal
  ["#FFF0F5", "#AD1457"], // Light rose with dark rose
  ["#F9FBE7", "#827717"], // Very light lime with olive
  ["#E3F2FD", "#1565C0"], // Light blue with medium blue
];

export const createColorMap = (
  keys: string[],
): Record<string, [string, string]> => {
  const assignedColors = new Set<string>();
  return keys.reduce(
    (acc, key) => {
      if (acc[key] === undefined) {
        const nextColor =
          predefinedColors.find((color) => !assignedColors.has(color[0])) ||
          defaultColor;
        assignedColors.add(nextColor[0]);
        acc[key] = nextColor as [string, string];
      }
      return acc;
    },
    {} as Record<string, [string, string]>,
  );
};

export const getEarliestEventOfWeek = (
  events: CalendarEvents[],
  date: Date,
) => {
  const startTime = startOfWeek(date, { weekStartsOn: 0 }).getTime();
  const endTime = endOfWeek(date, { weekStartsOn: 0 }).getTime();
  return events.reduce((earliest: string, event: CalendarEvents) => {
    if (event.allDay) {
      return earliest;
    }
    const eventStart = Number(event.startSec) * 1000;
    // We try to make sure > 24 hour events are "all day" events, but it's not
    // guaranteed (ex: Google OOO events).  For this scrolling logic we'll ignore them.
    const eventEnd = Number(event.endSec) * 1000;
    const durationInSeconds = (eventEnd - eventStart) / 1000;

    if (durationInSeconds >= 86400) {
      return earliest;
    }
    if (eventStart > startTime && eventStart < endTime) {
      const eventTime = format(
        parseISO(new Date(eventStart).toISOString()),
        "HH:mm",
      );
      return eventTime < earliest ? eventTime : earliest;
    }
    return earliest;
  }, "07:30"); // Start at 7:30 AM if there are no earlier times
};

export const convertTimeToDate = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  const today = new Date();
  today.setHours(hours, minutes, 0, 0);
  return today;
};

// A generic array [object multi key] dedupe. O(n) time.
// Faster than some lodash dedupes that might use JSON.stringify under the hood.
export const deduplicate = <T, K extends keyof T>(arr: T[], keys: K[]): T[] => {
  const map = new Map<string, boolean>();
  return arr.filter((item) => {
    const compositeKey = keys.map((key) => item[key]).join("|");
    if (map.has(compositeKey)) return false;
    map.set(compositeKey, true);
    return true;
  });
};

export interface WeekBoundaryInfo {
  currentMonth: number;
  currentYear: number;
  otherMonth: number | null;
  otherYear: number | null;
}

export const getWeekBoundaryInfo = (date: Date): WeekBoundaryInfo => {
  const currentMonth = date.getMonth() + 1;
  const currentYear = date.getFullYear();

  // Check one week after
  const oneWeekLater = new Date(date);
  oneWeekLater.setDate(oneWeekLater.getDate() + 7);
  const oneWeekMonth = oneWeekLater.getMonth() + 1;
  let oneWeekYear = oneWeekLater.getFullYear();
  if (oneWeekMonth > 12) {
    oneWeekYear += 1;
  }

  // Check one week before
  const oneWeekBefore = new Date(date);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
  const oneWeekBeforeMonth = oneWeekBefore.getMonth() + 1;
  let oneWeekBeforeYear = oneWeekBefore.getFullYear();
  if (oneWeekBeforeMonth < 1) {
    oneWeekBeforeYear -= 1;
  }

  // Check if we need to fetch from another month
  const months = new Set([currentMonth, oneWeekMonth, oneWeekBeforeMonth]);
  if (months.size > 1) {
    // Find the month that's different from currentMonth
    const otherMonth =
      [oneWeekMonth, oneWeekBeforeMonth].find((m) => m !== currentMonth) ??
      null;
    const otherYear =
      otherMonth === oneWeekMonth ? oneWeekYear : oneWeekBeforeYear;

    return {
      currentMonth,
      currentYear,
      otherMonth,
      otherYear,
    };
  }

  return {
    currentMonth,
    currentYear,
    otherMonth: null,
    otherYear: null,
  };
};

export const formatReminders = (
  reminders: { value: number; unit: string }[],
): string[] => {
  return reminders.map((reminder) => {
    const { value, unit } = reminder;

    if (value === 0) {
      return "At time of event";
    }

    // Remove trailing 's' from unit if present, then use pluralize helper
    const singularUnit = unit.replace(/s$/, "");
    const formattedUnit = pluralize(singularUnit, value);

    return `${value} ${formattedUnit}`;
  });
};
